import React, { useEffect, useContext, useState, useRef } from "react";
import { StoreContext } from "./../../store/Store";


import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";

import { BiPlusCircle, BiIdCard } from "react-icons/bi";
import IBadgeTemplate from "../../dataProvider/interfaces/IBadgeTemplate";
import { Loading, useRedirect } from "react-admin";
import { routePaths } from "../../baseLayout/SidebarMenu";
import { useParams } from "react-router";
import { APIContext } from "../../store/APIStore";
import { BiTrash } from "react-icons/bi";
import { IoMdCopy } from "react-icons/io";
import SimpleActionDialog, {
  Props as dialogType,
  actionDialogType,
} from "./../../components/SimpleActionDialog/SimpleActionDialog";
import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";
import { PERMISSIONS } from "../../roles";
import { useHasPermissions } from "../../hooks/useHasPermissions";
import { theme } from "../../theme";
import { badgeTemplateList as classes} from "./styles";

const BadgeTemplatesList = () => {
  const [state, dispatch] = useContext(StoreContext);
  const api = useContext(APIContext);
  const [templates, setTemplates] = useState<IBadgeTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmationDialog, setConfirmationDialog] = useState<dialogType>({
    open: false,
    title: "",
    type: actionDialogType.REGULAR,
    isLoading: false,
    onConfirm: () => {},
  });
  const redirect = useRedirect();
  const notificationRef = useRef<any>();

  const { eventId } = useParams() as any;

  // permissions
  const canManageBadgeDesign = useHasPermissions(
    PERMISSIONS.CAN_MANAGE_BADGE_DESIGN
  );

  // redirect if no permission to access this page
  useEffect(() => {
    if (canManageBadgeDesign === false) {
      redirect(`/${routePaths.ACTIVE_EVENTS}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageBadgeDesign]);

  // load data
  useEffect(() => {
    if (eventId && !state.selectedEvent) {
      fetchSelectedEvent();
    }

    if (state.selectedEvent) {
      fetchTemplatesList();
    }

    return () => {
      setTemplates([]);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedEvent]);

  const fetchTemplatesList = async () => {
    setLoading(true);
    const data = (await api.badgeTemplateService.getBadgeTemplates(
      state.selectedEvent.id
    )) as any;
    setTemplates(data);
    setLoading(false);
  };

  const fetchSelectedEvent = async () => {
    try {
      const event = await api.eventService.getEvent({ id: eventId });
      dispatch({ type: "SET_SELECTED_EVENT", payload: event });
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleDialogClose = () => {
    setConfirmationDialog({
      ...confirmationDialog,
      open: false,
    });
  };

  const handleDeleteTemplate = async (templateId: number) => {
    try {
      await api.badgeTemplateService.deleteTemplate(
        templateId
      );
      fetchTemplatesList();
      setConfirmationDialog({
        ...confirmationDialog,
        open: false,
      });
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const onDuplicateClick = async (template: IBadgeTemplate) => {
    try {
      const newTemplate = await api.badgeTemplateService.createTemplate({
        badgeFormat: template.badgeFormat.id,
        event_id: template.event_id,
        name: `${template.name}-copy`,
        xmlBackSideTemplate: template.xmlBackSideTemplate,
        xmlFrontSideTemplate: template.xmlFrontSideTemplate,
      });

      if (newTemplate) {
        redirect(
          `/${routePaths.EVENTS}/${state.selectedEvent.id}/${routePaths.BADGE_TEMPLATES}/${newTemplate.id}`
        );
      } else {
        notificationRef.current?.show(
          "Something went wrong!",
          notificationType.Error
        );
      }
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const onDeleteClick = (templateId: number) => {
    setConfirmationDialog({
      ...confirmationDialog,
      open: true,
      title: "Are you sure you want to delete this template?",
      icon: <BiTrash size="45" />,
      type: actionDialogType.WARNING,
      isLoading: false,
      onConfirm: () => {
        handleDeleteTemplate(templateId);
      },
      onCancel: handleDialogClose,
    });
  };

  return (
    <>
      <Box sx={classes.wrapper}>
        <Box sx={classes.topActions}>
          <Typography component="h3" variant="h3">
            Design Templates
          </Typography>

          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<BiPlusCircle />}
            onClick={(event) => {
              redirect(
                `/${routePaths.EVENTS}/${state.selectedEvent.id}/${routePaths.BADGE_DESIGNER}`
              );
            }}
          >
            Add design
          </Button>
        </Box>
        {!loading && (
          <Box paddingTop="20px">
            {templates.length > 0 && (
              <Grid container spacing={3}>
                {templates.map((template) => {
                  return (
                    <Grid key={template.id} item xs={4}>
                      <Card
                        sx={classes.cardItem}
                        onClick={(e: any) => {
                          redirect(
                            `/${routePaths.EVENTS}/${state.selectedEvent.id}/${routePaths.BADGE_TEMPLATES}/${template.id}`
                          );
                        }}
                      >
                        <CardContent sx={{
                          display: "flex",
                          p: "25px",
                          position: "relative",
                          // height: "calc(100% - 50px)",
                    
                          '&:hover .cardActions': {
                            visibility: "visible",
                          },
                        }
                        
                        } className="cardContent">
                          <Box width="75px">
                            <BiIdCard size="40px" />
                          </Box>

                          <Box width="calc(100% - 75px)">
                            <Typography
                              variant="h6"
                              component="h6"
                              align="left"
                            >
                              {template.name}
                            </Typography>
                            <Typography align="left">
                              {template.badgeFormat.name}
                            </Typography>
                          </Box>

                          <Box className="cardActions" sx={{
                            position: "absolute",
                            bottom: "0px",
                            right: "7px",
                            color: theme.palette.primary.main,
                            visibility: "hidden",
                          }}>
                            <IoMdCopy
                              size="20"
                              title="Duplicate"
                              onClick={(e: any) => {
                                e.stopPropagation();
                                onDuplicateClick(template);
                              }}
                            />

                            <BiTrash
                              size="20"
                              title="Delete"
                              style={{ marginLeft: "5px" }}
                              onClick={(e: any) => {
                                e.stopPropagation();
                                onDeleteClick(template.id);
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
            {templates.length === 0 && <Box>No templates found</Box>}
          </Box>
        )}
        {loading && <Loading />}
      </Box>
      {confirmationDialog.open && (
        <SimpleActionDialog
          type={confirmationDialog.type}
          icon={confirmationDialog.icon}
          open={confirmationDialog.open}
          title={confirmationDialog.title}
          isLoading={confirmationDialog.isLoading}
          onConfirm={confirmationDialog.onConfirm}
          onCancel={confirmationDialog.onCancel}
          onClose={handleDialogClose}
          content={confirmationDialog.content}
          confirmLabel={confirmationDialog.confirmLabel}
        />
      )}

      <NotificationMessage ref={notificationRef} />
    </>
  );
};

export default BadgeTemplatesList;
